import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ContentLayout } from "@/components/layout/ContentLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { ImageUpload } from "@/components/ui/image-upload";
import { uploadImageToS3Optimized } from "@/services/imageService";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const languageFlags = {
  tr: "🇹🇷",
  en: "🇬🇧",
  ar: "🇸🇦"
};

interface ProjectData {
  title: string;
  storage_path: string;
}

export default function EditHomeProject() {
  const { id } = useParams();
  const [title, setTitle] = useState({
    tr: "",
    en: "",
    ar: ""
  });
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  const [newImage, setNewImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchProject();
    }
  }, [id]);

  async function fetchProject() {
    setLoading(true);
    const { data, error } = await supabase
      .from("home_projects")
      .select("title, storage_path")
      .eq("id", id)
      .single();
    if (error) {
      toast({ title: "Hata", description: "Proje yüklenemedi.", variant: "destructive" });
    } else if (data) {
      setTitle(typeof data.title === 'string' ? JSON.parse(data.title) : data.title || { tr: "", en: "", ar: "" });
      setCurrentImageUrl(data.storage_path || "");
    }
    setLoading(false);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let imageUrl = currentImageUrl;

      // If new image is selected, upload optimized image to S3
      if (newImage) {
        imageUrl = await uploadImageToS3Optimized(newImage, 'home-projects');
      }

      const projectData: ProjectData = {
        title: JSON.stringify(title),
        storage_path: imageUrl
      };

      const { error } = await supabase
        .from("home_projects")
        .update(projectData)
        .eq("id", id);

      if (error) throw error;

      toast({ 
        title: "Başarılı", 
        description: "Proje başarıyla güncellendi." 
      });
      navigate("/content/home/projects");
    } catch (error) {
      console.error('Error updating project:', error);
      toast({ 
        title: "Hata", 
        description: "Proje güncellenirken bir hata oluştu.", 
        variant: "destructive" 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContentLayout title="Projeyi Düzenle" backLink="/content/home/projects">
      {loading ? (
        <div className="p-6">Yükleniyor...</div>
      ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <Tabs defaultValue="tr" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="tr">{languageFlags.tr} Türkçe</TabsTrigger>
                  <TabsTrigger value="en">{languageFlags.en} English</TabsTrigger>
                  <TabsTrigger value="ar">{languageFlags.ar} العربية</TabsTrigger>
                </TabsList>
                <TabsContent value="tr" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title-tr">Başlık (Türkçe)</Label>
                    <Input
                      id="title-tr"
                      placeholder="Proje başlığı"
                      value={title.tr}
                      onChange={(e) => setTitle({ ...title, tr: e.target.value })}
                      required
                    />
                  </div>
                </TabsContent>
                <TabsContent value="en" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title-en">Title (English)</Label>
                    <Input
                      id="title-en"
                      placeholder="Project title"
                      value={title.en}
                      onChange={(e) => setTitle({ ...title, en: e.target.value })}
                      required
                    />
                  </div>
                </TabsContent>
                <TabsContent value="ar" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title-ar">العنوان (العربية)</Label>
                    <Input
                      id="title-ar"
                      placeholder="عنوان المشروع"
                      value={title.ar}
                      onChange={(e) => setTitle({ ...title, ar: e.target.value })}
                      required
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            <div>
              <Card>
                <CardContent className="p-6">
                  <Label>Proje Görseli</Label>
                  {currentImageUrl && (
                    <div className="mt-2 mb-4">
                      <img 
                        src={currentImageUrl} 
                        alt="Mevcut görsel" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    </div>
                  )}
                  <ImageUpload
                    value={newImage}
                    onChange={setNewImage}
                    className="mt-2"
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate("/content/home/projects")}
            >
              İptal
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        </form>
      )}
    </ContentLayout>
  );
}
